/**
 * The header's icon-plus-dot trigger — shared by `FindingsButton` and `ComplianceButton`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS: the pills were 250px an operator could not get back
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `FindingsBadge` and `ComplianceBadge` used to spell their numbers out inline —
 * `⚠ 2 findings` and `● 83% partial | 2 open` — as two wide pills sitting beside the alarm
 * bell. Together with `HeaderScope` that made four controls fighting for the same row, and
 * the two pills were the only ones of the four that did not fit a 32px header control: they
 * carried their whole answer in the chrome instead of behind a click. Reducing them to an
 * icon with a dot, matching the bell's own footprint, reclaims that width without losing the
 * numbers — they move into a popover, which is what this file's `onClick` opens.
 *
 * ONE COMPONENT, written the moment there was a second button shaped like it. They sit a few
 * pixels apart in the header, so any drift between them — a badge one pixel larger, a hover
 * that lifts differently — is immediately visible. The severity logic that differs between
 * Findings and Compliance (what counts as "quiet", which band maps to which tone) stays in
 * the callers; the geometry, the badge and the button's own states live here.
 *
 * `count === 0` drops the badge entirely and quiets the icon to `--muted-fg`. A grey icon
 * with no mark is the header's way of saying nothing is wrong; a badge that is present but
 * reads "0" is not — a greyed-out zero is the loudest way to say nothing.
 *
 * A DOT, ALWAYS, FOR BOTH CALLERS — unlike the alarm bell, which still earns a number. A
 * count is worth the space only when the reader acts differently at 4 than at 40, which is
 * true of alarms (a live queue an operator triages by size) and of neither Findings (a
 * backlog, not triaged from the header) nor Compliance (a score, not a count at all — see
 * `ComplianceButton` for why its own "count" is a 0/1 gate rather than a real figure). The
 * `count` prop still exists here because it is what decides whether the icon goes quiet, not
 * because this file expects to print it; nothing in `FindingsButton` or `ComplianceButton`
 * passes `dot={false}`, so the numbered-badge branch below is exercised by neither caller
 * today. It stays rather than being deleted so the two rendering modes remain visibly one
 * component instead of silently diverging into two.
 *
 * NO `pulse` PROP. The alarm bell is the only thing in this row allowed to glow — three
 * animated icons is three things shouting, and nothing then reads as urgent — and Findings
 * and Compliance are both static by that rule, not by an accident of what happened to get
 * wired up. A prop with no caller that could ever legitimately pass `true` is not a
 * parameter, it is a dare, so it is left out rather than ported and ignored.
 */
import { forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { Icon, type IconName } from './Icon';

export type StatTone = 'danger' | 'warn' | 'info' | 'success' | 'muted';

/*
 * THE BADGE FILL, which is not `--{tone}-rgb`.
 *
 * `--*-rgb` follows the theme, and dark theme's is tuned to sit on a near-black card — dark's
 * `--danger-rgb` is `#f87171`, and white ink on that measures 2.67:1, under the 4.5:1 floor.
 * `--*-badge` is the theme-independent set built for exactly this: a fill that carries a
 * fixed white ink (`--severity-fg`) in both themes. See `tokens.css` for the measured ratios.
 */
const BADGE: Record<StatTone, string> = {
  danger: '--danger-badge',
  warn: '--warn-badge',
  info: '--info-badge',
  success: '--success-badge',
  muted: '--na-badge',
};

export interface HeaderStatButtonProps {
  icon: IconName;
  /** Drives the quiet state (`count === 0`) and, in the numbered branch, the printed figure. */
  count: number;
  tone: StatTone;
  /** What it is, for the accessible name — "Findings", "Compliance". */
  label: string;
  /** The full sentence: the tooltip, and the second half of the accessible name. */
  detail: string;
  /** Dot instead of a number. Both current callers pass this. See the file comment. */
  dot?: boolean;
  /** Whether the popover this button opens is currently open — drives `aria-expanded`. */
  open: boolean;
  onClick: () => void;
  className?: string | undefined;
}

export const HeaderStatButton = forwardRef<HTMLButtonElement, HeaderStatButtonProps>(
  function HeaderStatButton(
    { icon, count, tone, label, detail, dot, open, onClick, className },
    ref,
  ) {
    const quiet = count === 0;
    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        title={detail}
        aria-label={`${label}: ${detail}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn('lm-hov relative flex shrink-0 items-center justify-center rounded-[var(--radius-sm)]', className)}
        style={{
          width: 'var(--header-ctl-h)',
          height: 'var(--header-ctl-h)',
          color: quiet ? 'var(--muted-fg)' : 'var(--fg)',
        }}
      >
        <Icon name={icon} size={16} />
        {!quiet &&
          (dot ? (
            /*
              RINGED IN THE HEADER'S OWN GROUND, like the bell's unseen-count badge. The
              header paints solid `--card` (`.lm-glass` resolves to it — there is no actual
              blur left in this build, see `app.css`), so the ring genuinely sits on the
              colour it is drawn in, not a guess at one.
            */
            <span
              aria-hidden
              className="absolute top-[5px] right-[5px] size-[7px] rounded-full"
              style={{
                background: `rgb(var(${BADGE[tone]}))`,
                boxShadow: '0 0 0 2px var(--card)',
              }}
            />
          ) : (
            <span
              className="lm-num absolute top-0.5 right-0 flex min-w-[17px] items-center justify-center rounded-[var(--radius-pill)] border px-[3px] text-[10.5px] font-bold"
              style={{
                background: `rgb(var(${BADGE[tone]}))`,
                borderColor: 'var(--card-opaque)',
                color: 'var(--severity-fg)',
              }}
            >
              {count > 99 ? '99+' : count}
            </span>
          ))}
      </button>
    );
  },
);
